var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { DatePicker, Paper, Stacker } from '@honeycomb/ui-core';
import { useSearch, DateSearchType, currentHotelFilterSelectionsVar, useHotelTeaserPrices, TripType, useCurrentTripId, useGlobalContext, } from '@honeycomb/data';
import { DateTime } from 'luxon';
import { priceCalendarSearchCreatedRouting } from '@honeycomb/routing';
import { HotelBasketActionType, useHotelBasketContext } from '../HotelBasketProvider';
import { DateFormats, getCurrentDate, getFormattedDate } from '../../../utils/date-utils';
import { getFormattedStartDate } from '../../../utils/formatDate';
import { HotelPriceCalendarDay, HotelPriceCalendarHeader } from '.';
import { calendarSideEffect, getDealDates, getDestinations, ISODATEFORMAT, handleCurrentCalendarPrice, } from '../../hotel-price-calendar/calendarUtil';
import { useHotelDetailsContext } from '../HotelDetails/HotelDetails.context';
import { HotelPriceCalendarSkeleton } from '../../hotel-price-calendar';
import { HotelDetailsActionType } from '../HotelDetails';
export function HotelPriceCalendar(_a) {
    var _b, _c, _d, _e, _f;
    var { hotel } = _a, other = __rest(_a, ["hotel"]);
    const { estabId } = hotel;
    const { configSettings: { PricingCalendarVisibilityThreshold }, } = useGlobalContext();
    const [searchState, setSearch] = useSearch([priceCalendarSearchCreatedRouting(estabId)]);
    const { hotelBasket, dispatch } = useHotelBasketContext();
    const detailsContext = useHotelDetailsContext();
    const [initialMonth, setInitialMonth] = useState();
    const [calendarMonthYear, setCalendarMonthYear] = useState('');
    const [hotelTeaserPricesRequest, setHotelTeaserPricesRequest] = useState({
        estabID: Number(estabId),
    });
    const { tripType: trip } = searchState;
    const { createSearch, loading: searchLoading } = setSearch;
    const { selectedRooms, waitingForRooms } = hotelBasket;
    // Keeping this from reactive var as its quicker than waiting fro trip state from context, as it waits for data to be set.
    const [tripState] = useCurrentTripId();
    const tripStateId = (_b = tripState.data) === null || _b === void 0 ? void 0 : _b.currentTripStateId;
    const pricingSummary = (_c = selectedRooms === null || selectedRooms === void 0 ? void 0 : selectedRooms[0]) === null || _c === void 0 ? void 0 : _c.pricingSummary;
    let calendarStartDate = '';
    // If we have the pricing summary get the date (utilises checkin and departure date) for multi search, else use the search dates.
    if (pricingSummary)
        calendarStartDate = getFormattedStartDate(pricingSummary, searchState);
    else {
        const { startDate, wholeMonthStartDate } = searchState;
        calendarStartDate =
            startDate || wholeMonthStartDate
                ? String(getFormattedDate(DateFormats.Shortdaydateyear, startDate || wholeMonthStartDate || ''))
                : '';
    }
    const teaserPricesResults = useHotelTeaserPrices(hotelTeaserPricesRequest, [
        (data) => {
            calendarSideEffect(data, detailsContext, parseInt(PricingCalendarVisibilityThreshold));
        },
        (data) => {
            handleCurrentCalendarPrice(data, calendarStartDate, tripStateId, estabId);
        },
    ], [
        (_error) => {
            detailsContext.dispatch({ type: HotelDetailsActionType.SET_PRICING_CALENDAR_TOGGLE, payload: false });
            detailsContext.dispatch({
                type: HotelDetailsActionType.SET_PRICING_CALENDAR_INITIAL_LOAD,
                payload: true,
            });
        },
    ], !(trip === TripType.Holiday || trip === TripType.Hotel));
    const { data: hotelTeaserPrices, loading: calendarloading } = teaserPricesResults;
    const loading = calendarloading || searchLoading || waitingForRooms;
    useEffect(() => {
        if (calendarStartDate) {
            const startDate = new Date(calendarStartDate);
            setCalendarMonthYear(`${startDate.getMonth() + 1}-${startDate.getFullYear()}`);
            setInitialMonth(startDate);
        }
    }, [calendarStartDate]);
    // When either calendar month or trip state changes we make a new teaser request to get price.
    useEffect(() => {
        const teaserPricesRequest = {
            estabID: Number(estabId),
            calendarMonthYear,
            tripStateId,
            filters: currentHotelFilterSelectionsVar(),
        };
        setHotelTeaserPricesRequest(teaserPricesRequest);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [calendarMonthYear, tripStateId]);
    const startDate = useMemo(() => {
        // TODO: check why calendarStartDate is evaluating to string "undefined"
        if (!calendarStartDate || calendarStartDate === 'undefined') {
            return new Date();
        }
        const start = new Date(calendarStartDate);
        return new Date() > start ? new Date() : start;
    }, [calendarStartDate]);
    const endDate = useMemo(() => {
        const wholeMonthStartDate = searchState.wholeMonthStartDate
            ? new Date() > new Date(searchState.wholeMonthStartDate)
                ? new Date()
                : new Date(searchState.wholeMonthStartDate)
            : new Date();
        wholeMonthStartDate.setDate(wholeMonthStartDate.getDate() + (searchState.duration || 0));
        return (pricingSummary === null || pricingSummary === void 0 ? void 0 : pricingSummary.checkOutDate) || searchState.endDate
            ? new Date((pricingSummary === null || pricingSummary === void 0 ? void 0 : pricingSummary.checkOutDate) || searchState.endDate)
            : wholeMonthStartDate;
    }, [pricingSummary === null || pricingSummary === void 0 ? void 0 : pricingSummary.checkOutDate, searchState.duration, searchState.endDate, searchState.wholeMonthStartDate]);
    const [month, setMonth] = useState(DateTime.fromISO(startDate.toISOString()).month);
    const calendarPrices = (_e = (_d = hotelTeaserPrices === null || hotelTeaserPrices === void 0 ? void 0 : hotelTeaserPrices.hotelTeaserPrices) === null || _d === void 0 ? void 0 : _d.prices) === null || _e === void 0 ? void 0 : _e.filter((dateValue) => {
        return month === DateTime.fromISO(dateValue.date).month;
    });
    const searchStartDate = startDate.setHours(0, 0, 0, 0);
    const searchEndDate = endDate.setHours(0, 0, 0, 0);
    const currentDate = new Date(getCurrentDate().toISO()).setHours(0, 0, 0, 0);
    useEffect(() => {
        setMonth(DateTime.fromJSDate(startDate).month);
    }, [startDate]);
    const destinations = useMemo(() => {
        return getDestinations(hotel, searchState.destinations);
    }, [hotel, searchState.destinations]);
    const minDealPriceArray = useMemo(() => getDealDates(startDate, calendarPrices), [calendarPrices, startDate]);
    const isDealOnStartDate = minDealPriceArray === null || minDealPriceArray === void 0 ? void 0 : minDealPriceArray.some((date) => String(new Date(date)) === String(startDate));
    const departDate = DateTime.fromISO(startDate.toISOString()).toFormat(`dd'/'MM'/'yy`);
    const customDayButton = useCallback((_a) => {
        var { day, modifiers } = _a, buttonProps = __rest(_a, ["day", "modifiers"]);
        return (React.createElement(HotelPriceCalendarDay, Object.assign({ calendarPrices: calendarPrices, minDealPriceArray: minDealPriceArray, searchStartDate: searchStartDate, currentDate: currentDate, searchEndDate: searchEndDate, estabId: estabId, day: day, modifiers: modifiers }, buttonProps)));
    }, [calendarPrices, currentDate, estabId, minDealPriceArray, searchEndDate, searchStartDate]);
    const duration = Math.round((_f = DateTime.fromISO(endDate.toISOString()).diff(DateTime.fromISO(startDate.toISOString()), ['days']).toObject()
        .days) !== null && _f !== void 0 ? _f : 1);
    // When we update the calendar month.
    const handleMonthChange = (date) => {
        const calendarDate = DateTime.fromISO(date.toISOString());
        setMonth(calendarDate.month);
        setInitialMonth(date);
        setCalendarMonthYear(`${calendarDate.month}-${calendarDate.year}`);
        setHotelTeaserPricesRequest((prevrequest) => (Object.assign(Object.assign({}, prevrequest), { calendarMonthYear: `${calendarDate.month}-${calendarDate.year}` })));
    };
    // On clicking on date in calendar.
    const onSelectDate = (day, dur, tripType, departureAirports) => {
        const updatedStartDate = DateTime.fromJSDate(day).toFormat(ISODATEFORMAT);
        const updatedEndDate = DateTime.fromMillis(day.getTime() + dur * 24 * 60 * 60 * 1000).toFormat(ISODATEFORMAT);
        if (!updatedStartDate || !updatedEndDate)
            return;
        dispatch({ type: HotelBasketActionType.SET_WAITING_FOR_ROOM, payload: true });
        const updatedSearchState = Object.assign(Object.assign(Object.assign(Object.assign({}, searchState), { startDate: updatedStartDate, endDate: updatedEndDate, destinations, dateSearchType: DateSearchType.ChooseDate, duration: null, flexibleDays: null }), ((departureAirports === null || departureAirports === void 0 ? void 0 : departureAirports.length) && { departureAirports, tripType: TripType.Holiday })), (tripType && { tripType }));
        createSearch(updatedSearchState);
    };
    // On selecting duration from dropdown
    const onChangeDuration = (days) => {
        const updatedEndDate = DateTime.fromMillis(DateTime.fromISO(startDate.toISOString()).toMillis() + days * 24 * 60 * 60 * 1000).toFormat(ISODATEFORMAT);
        if (!updatedEndDate)
            return;
        dispatch({ type: HotelBasketActionType.SET_WAITING_FOR_ROOM, payload: true });
        const updatedSearchState = Object.assign(Object.assign({}, searchState), { startDate: DateTime.fromISO(startDate.toISOString()).toFormat(ISODATEFORMAT), endDate: updatedEndDate, destinations, dateSearchType: DateSearchType.ChooseDate, duration: null, flexibleDays: null });
        createSearch(updatedSearchState);
    };
    if (!loading && !calendarPrices) {
        return null;
    }
    // Todo: full width on price calendar.
    return (React.createElement(Paper, Object.assign({ py: 5, defaultGutters: true }, other),
        React.createElement(Stacker, { spacing: 4, "data-id": "calendar_with_price" },
            React.createElement(HotelPriceCalendarHeader, { departDate: departDate, isDealOnStartDate: Boolean(isDealOnStartDate), onChangeDuration: onChangeDuration, loading: loading, defaultDuration: duration }),
            loading ? (React.createElement(HotelPriceCalendarSkeleton, { w: "100%" })) : (React.createElement(DatePicker, { onSelectDate: (day) => {
                    onSelectDate(day, duration !== null && duration !== void 0 ? duration : 7);
                }, defaultMonth: initialMonth, customDayButton: customDayButton, onMonthChange: handleMonthChange, fullWidth: true, dayRowSpacing: 4, dayColumnSpacing: { xs: 1, s: 2, m: 3, l: 4 } })))));
}
